import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ComingSoon — pre-launch holding page.
 * Sage + Forest palette, Atom Serif headline, Atom Sans body.
 * Captures visitor email, fires a Resend confirmation + team notification.
 */
export default function ComingSoon() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [errorMsg, setErrorMsg] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      setStatus("error");
      setErrorMsg("Please enter a valid email address.");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${API}/api/marketing/notify`, {
        email: email.trim(),
        name: name.trim() || null,
      });
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err?.response?.data?.detail ||
          "Something went wrong. Please try again."
      );
    }
  };

  return (
    <div data-testid="coming-soon" style={styles.wrap}>
      <div style={styles.lockup}>
        <Link to="/admin/login" style={styles.lockupLink} aria-label="Crowther">
          <span style={styles.brand}>Crowther</span>
        </Link>
      </div>

      <main style={styles.centre}>
        <p style={styles.eyebrow}>Coming soon</p>
        <h1 style={styles.title}>
          A new era of <em style={styles.em}>sovereign intelligence</em> is
          being built.
        </h1>
        <p style={styles.sub}>
          Crowther is preparing to open. Leave your email and we will let you
          know the moment we go live.
        </p>

        {status !== "done" ? (
          <form onSubmit={onSubmit} style={styles.form} data-testid="coming-soon-form">
            <input
              type="text"
              placeholder="Your name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={styles.input}
              data-testid="coming-soon-name"
              autoComplete="name"
            />
            <input
              type="email"
              required
              placeholder="Your work email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              data-testid="coming-soon-email"
              autoComplete="email"
            />
            <button
              type="submit"
              style={{
                ...styles.btn,
                opacity: status === "loading" ? 0.6 : 1,
                cursor: status === "loading" ? "wait" : "pointer",
              }}
              disabled={status === "loading"}
              data-testid="coming-soon-submit"
            >
              {status === "loading" ? "Sending…" : "Notify me →"}
            </button>
            {status === "error" && (
              <div style={styles.err} data-testid="coming-soon-error">
                {errorMsg}
              </div>
            )}
          </form>
        ) : (
          <div style={styles.done} data-testid="coming-soon-done">
            <p style={styles.doneTitle}>You are on the list.</p>
            <p style={styles.doneSub}>
              We have sent a note to your inbox. When Crowther opens, you will be
              among the first to know.
            </p>
          </div>
        )}

        <div style={styles.footRow}>
          <span style={styles.footTag}>Human insight, amplified.</span>
        </div>
      </main>

      <footer style={styles.footer}>
        <span>© {new Date().getFullYear()} Crowther</span>
        <span style={{ opacity: 0.6 }}>
          <a href="mailto:hello@crowtherlabs.com" style={styles.footLink}>
            hello@crowtherlabs.com
          </a>
        </span>
      </footer>
    </div>
  );
}

const styles = {
  wrap: {
    minHeight: "100vh",
    background: "#161E1B",
    color: "#F6F1EA",
    fontFamily: "var(--font-sans)",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    overflow: "hidden",
    padding: "40px 48px",
    boxSizing: "border-box",
  },
  lockup: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
  },
  lockupLink: { textDecoration: "none", color: "inherit" },
  brand: {
    fontSize: 18,
    fontWeight: 500,
    letterSpacing: "-0.005em",
    color: "#F6F1EA",
  },
  centre: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    maxWidth: 780,
    margin: "0 auto",
    padding: "0 16px",
  },
  eyebrow: {
    fontSize: 11,
    letterSpacing: "0.28em",
    textTransform: "uppercase",
    color: "#A1AC9C",
    marginBottom: 24,
    fontWeight: 500,
  },
  title: {
    fontFamily: "var(--font-serif, 'Atom Serif', Georgia, serif)",
    fontSize: "clamp(34px, 5vw, 68px)",
    lineHeight: 1.06,
    letterSpacing: "-0.02em",
    fontWeight: 400,
    color: "#FFFFFF",
    margin: "0 0 24px",
    maxWidth: "18ch",
  },
  em: { fontStyle: "italic", color: "#A1AC9C" },
  sub: {
    fontSize: 16,
    lineHeight: 1.6,
    color: "rgba(246,241,234,0.72)",
    marginBottom: 42,
    maxWidth: 520,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    width: "100%",
    maxWidth: 420,
  },
  input: {
    padding: "16px 20px",
    fontSize: 15,
    fontFamily: "var(--font-sans)",
    background: "rgba(246,241,234,0.05)",
    color: "#F6F1EA",
    border: "1px solid rgba(246,241,234,0.18)",
    borderRadius: 999,
    outline: "none",
    transition: "border-color 200ms, background 200ms",
  },
  btn: {
    marginTop: 4,
    padding: "16px 20px",
    fontSize: 14,
    fontFamily: "var(--font-sans)",
    fontWeight: 500,
    letterSpacing: "0.04em",
    background: "#A1AC9C",
    color: "#161E1B",
    border: 0,
    borderRadius: 999,
    cursor: "pointer",
    transition: "background 200ms, color 200ms, transform 200ms",
  },
  err: {
    marginTop: 8,
    fontSize: 13,
    color: "#F6C6B7",
    textAlign: "left",
    paddingLeft: 20,
  },
  done: {
    maxWidth: 460,
  },
  doneTitle: {
    fontFamily: "var(--font-serif)",
    fontSize: 24,
    color: "#A1AC9C",
    marginBottom: 12,
    fontWeight: 400,
  },
  doneSub: {
    fontSize: 15,
    lineHeight: 1.6,
    color: "rgba(246,241,234,0.72)",
  },
  footRow: {
    marginTop: 60,
  },
  footTag: {
    fontSize: 12,
    letterSpacing: "0.06em",
    color: "rgba(246,241,234,0.4)",
    fontStyle: "italic",
    fontFamily: "var(--font-serif)",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 11,
    color: "rgba(246,241,234,0.5)",
    letterSpacing: "0.04em",
    paddingTop: 24,
    borderTop: "1px solid rgba(246,241,234,0.08)",
  },
  footLink: {
    color: "inherit",
    textDecoration: "none",
  },
};
